/* NASA Birthday Print Editor — full-screen personalization builder.
 *
 * A thestarposter.com/editor-style full-page experience: the customer picks
 * their birthday (month + day), sees the Hubble image NASA captured that day,
 * personalises the text overlay (heading, subheading, font, color, matte),
 * and adds the customised print to cart with line-item properties.
 *
 * Reuses the same canvas rendering logic as personalizer.js (scrim, tracking,
 * fit-to-width, matte) so the preview matches the server compositor.
 */
(function () {
  "use strict";

  /* ── DOM helpers ───────────────────────────────────────────────────────── */
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return root.querySelectorAll(sel); }
  function el(tag, cls) { var e = document.createElement(tag); if (cls) e.className = cls; return e; }

  /* ── Color / font helpers ──────────────────────────────────────────────── */
  function hexOf(list, id) { var f = list.find(function (x) { return x.id === id; }); return f ? f.hex : "#000"; }
  function resolveColor(state, idKey, customKey, colors) {
    if (state[customKey]) return state[customKey];
    return hexOf(colors, state[idKey]);
  }
  function familyOf(fonts, id) { var f = fonts.find(function (x) { return x.id === id; }); return f ? f.family : "sans-serif"; }
  // Extra caption line spacing in reference px. Mirrors config.py so the
  // preview and the print file agree; callers scale it to their canvas.
  function capGap(state, spec) {
    var v = state.caption_gap_px;
    if (v == null) v = (spec && spec.defaults) ? spec.defaults.caption_gap_px : 0;
    v = Number(v);
    return isNaN(v) ? 0 : v;
  }
  function fontsForRole(fonts, role) {
    return fonts.filter(function (f) {
      if (!f.roles || !f.roles.length) return true;
      return f.roles.indexOf(role) >= 0;
    });
  }
  function fontUppercase(fonts, fontId, layoutDefault) {
    var f = fonts.find(function (x) { return x.id === fontId; });
    if (f && Object.prototype.hasOwnProperty.call(f, "uppercase")) return !!f.uppercase;
    return layoutDefault !== false;
  }
  function fontWeight(fonts, fontId) {
    var f = fonts.find(function (x) { return x.id === fontId; });
    return (f && f.weight) ? f.weight : 400;
  }
  // Map a Printful style name (e.g. "Walnut Floating Frame Canvas") to a frame
  // preview kind + tone. Purely decorative (CSS around the canvas); never baked
  // into the saved print/cart image. (Ported from personalizer.js.)
  function frameSpec(styleName) {
    if (!styleName) return { kind: "none", tone: "none" };
    var lower = String(styleName).toLowerCase();
    if (lower.indexOf("print only") >= 0 || lower === "print") return { kind: "none", tone: "none" };
    var kind = lower.indexOf("floating") >= 0 ? "floating" : (lower.indexOf("frame") >= 0 ? "print" : "none");
    var tone = "black";
    if (lower.indexOf("walnut") >= 0) tone = "walnut";
    else if (lower.indexOf("wood") >= 0) tone = "wood";
    else if (lower.indexOf("white") >= 0) tone = "white";
    else if (lower.indexOf("black") >= 0) tone = "black";
    return { kind: kind, tone: tone };
  }

  /* ── Typography scaling ────────────────────────────────────────────────── */
  function scaledTypo(spec, H, state) {
    var typo = (spec.layout && spec.layout.typography) || {};
    var ref = typo.reference_height_px || 5000;
    var scale = H / ref;
    var headingPx = (state && state.heading_font_size_px) || typo.heading_font_px || 200;
    var subPx = (state && state.subheading_font_size_px) || typo.subheading_font_px || 90;
    var headTrack = (state && state.heading_tracking != null) ? state.heading_tracking : (typo.heading_tracking || 0.012);
    var subTrack = (state && state.subheading_tracking != null) ? state.subheading_tracking : (typo.subheading_tracking || 0.10);
    return {
      headingStart: Math.round(headingPx * scale),
      headingMin: Math.round((typo.min_heading_font_px || 60) * scale),
      subStart: Math.round(subPx * scale),
      subMin: Math.round((typo.min_subheading_font_px || 60) * scale),
      gap: Math.round((typo.gap_px || 18) * scale),
      margin: Math.round((typo.edge_margin_px || 280) * scale),
      boxX: typo.box_x_frac || 0.08,
      boxW: typo.box_w_frac || 0.84,
      headingTracking: headTrack,
      subTracking: subTrack
    };
  }

  function ratioForState(spec, state) {
    var size = state.size || state.ratio;
    if (spec.sizes && spec.sizes[size]) return spec.sizes[size].ratio;
    return size;
  }
  function previewAspect(spec, state) {
    var ratio = ratioForState(spec, state);
    var px = spec.print[ratio] && spec.print[ratio].px;
    if (px) return px[0] / px[1];
    return 0.8;
  }

  /* ── Text fitting (mirrors compositor) ─────────────────────────────────── */
  function fitText(ctx, text, family, weight, maxW, startSize, minSize, trackingFrac) {
    var step = Math.max(1, Math.round(startSize * 0.03));
    for (var size = startSize; size >= minSize; size -= step) {
      ctx.font = weight + " " + size + 'px "' + family + '", sans-serif';
      var tracking = trackingFrac * size;
      var w = ctx.measureText(text).width + tracking * Math.max(0, text.length - 1);
      if (w <= maxW) return { size: size, tracking: tracking };
    }
    return { size: minSize, tracking: trackingFrac * minSize };
  }

  // Distance from the top of a line to its alphabetic baseline, from the font's
  // own metrics. We do NOT use textBaseline "top": Safari and Chrome position
  // the "top" baseline from different metric tables, so the same canvas renders
  // text at slightly different heights per browser (heading↔subheading gap
  // looked smaller on mobile than desktop). Ported from personalizer.js.
  function topToBaseline(ctx, size) {
    try {
      var m = ctx.measureText("Hg");
      if (m && typeof m.fontBoundingBoxAscent === "number" && m.fontBoundingBoxAscent > 0) {
        return m.fontBoundingBoxAscent;
      }
    } catch (e) { /* fall through */ }
    return size * 0.8;
  }

  // Returns the drawn text's bounding box {x, y, w, h} in canvas space so the
  // caller can build tap hit-regions (tap heading/subheading to jump to input).
  function drawTracked(ctx, text, family, weight, size, tracking, color, align, boxLeft, boxW, y) {
    ctx.font = weight + " " + size + 'px "' + family + '", sans-serif';
    ctx.fillStyle = color;
    ctx.textBaseline = "alphabetic";
    var baseline = y + topToBaseline(ctx, size);   // y is the top of the line
    var total = ctx.measureText(text).width + tracking * Math.max(0, text.length - 1);
    var x = align === "center" ? boxLeft + (boxW - total) / 2
          : align === "right" ? boxLeft + (boxW - total) : boxLeft;
    var xStart = x;
    for (var i = 0; i < text.length; i++) {
      ctx.fillText(text[i], x, baseline);
      x += ctx.measureText(text[i]).width + tracking;
    }
    return { x: xStart, y: y, w: total, h: size * 1.15 };
  }

  /* ── Font loading ──────────────────────────────────────────────────────── */
  function loadCustomFonts(fonts, proxyBase) {
    var style = document.getElementById("bday-pz-fonts");
    if (!style) {
      style = document.createElement("style");
      style.id = "bday-pz-fonts";
      document.head.appendChild(style);
    }
    var rules = fonts.map(function (f) {
      var ext = f.file.toLowerCase().endsWith(".otf") ? "opentype" : "truetype";
      var url = proxyBase + "/fonts/" + encodeURIComponent(f.file);
      return "@font-face { font-family: '" + f.family + "'; font-weight: " +
        (f.weight || 400) + "; font-style: normal; src: url('" + url + "') format('" + ext + "'); }";
    });
    style.textContent = rules.join("\n");
    var waits = fonts.map(function (f) {
      try { return document.fonts.load((f.weight || 400) + " 32px '" + f.family + "'"); }
      catch (e) { return Promise.resolve(); }
    });
    return Promise.all(waits).catch(function () {}).then(function () {
      return document.fonts.ready;
    });
  }

  /* ── Month helpers ─────────────────────────────────────────────────────── */
  var MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  var DAYS_IN_MONTH = [31,29,31,30,31,30,31,31,30,31,30,31];

  function dateKey(m, d) {
    return String(m).padStart(2, "0") + "-" + String(d).padStart(2, "0");
  }

  /* ── Store-wide promotion ──────────────────────────────────────────────────
     Shopify's automatic discounts are invisible to both Liquid and the product
     JSON, so the storefront can't discover them: the only place they surface is
     the cart, which is far too late for someone still deciding. The theme
     therefore declares the running promotion on the mount element. An expiry is
     required so a forgotten setting can't keep advertising a discount that
     checkout no longer honours. */
  function readPromo(root) {
    var pct = parseFloat(root.getAttribute("data-promo-percent"));
    if (!(pct > 0 && pct < 100)) return null;
    var ends = root.getAttribute("data-promo-ends");
    if (ends) {
      var t = Date.parse(ends);
      if (isNaN(t) || t < Date.now()) return null;
    }
    return { percent: pct, label: root.getAttribute("data-promo-label") || "" };
  }

  /* Discount for one line in cents. Shopify allocates the discount to the line
     and truncates the fraction, so 25% of $43.99 is $10.99 and not $11.00 —
     matching that here keeps the builder and the cart to the same cent. */
  function promoDiscount(promo, cents) {
    if (!promo || typeof cents !== "number") return 0;
    return Math.floor(cents * promo.percent / 100);
  }

  /* Cents are always shown: a discounted price often lands on a round dollar and
     "$33 $43.99" beside its struck original reads like a typo. */
  function money(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  /* Trim a value we fill in on the customer's behalf to the limit their own
     typing is held to, breaking on a word so the result still reads as a title.
     Anything we prefill that fails validation is a dead end: the shopper is
     blocked from adding to cart by text they never wrote. */
  function clampText(text, max) {
    text = String(text == null ? "" : text).trim();
    if (!max || text.length <= max) return text;
    var cut = text.slice(0, max);
    var sp = cut.lastIndexOf(" ");
    return (sp > max * 0.6 ? cut.slice(0, sp) : cut).trim();
  }

  /* ── Toast ─────────────────────────────────────────────────────────────── */
  var toastEl = null;
  var toastTimer = null;
  function showToast(msg, actionLabel, onAction) {
    if (!toastEl) {
      toastEl = el("div", "bday-toast");
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = "";
    var text = el("span");
    text.textContent = msg;
    toastEl.appendChild(text);
    if (actionLabel && typeof onAction === "function") {
      var act = el("button", "bday-toast__action");
      act.type = "button";
      act.textContent = actionLabel;
      act.addEventListener("click", function () {
        toastEl.classList.remove("bday-toast--visible");
        onAction();
      });
      toastEl.appendChild(act);
    }
    toastEl.classList.add("bday-toast--visible");
    clearTimeout(toastTimer);
    // An offer worth acting on needs longer than a bare confirmation.
    toastTimer = setTimeout(function () {
      toastEl.classList.remove("bday-toast--visible");
    }, actionLabel ? 6000 : 3000);
  }

  /* ════════════════════════════════════════════════════════════════════════
   *  BirthdayEditor — main class
   * ════════════════════════════════════════════════════════════════════════ */
  function BirthdayEditor(root) {
    this.root = root;
    this.proxyBase = root.getAttribute("data-proxy-base") || "/apps/personalizer";
    this.handle = root.getAttribute("data-handle") || "nasa-birthday-print";

    this.catalog = null;     // MM-DD -> { title, caption, image, ... }
    this.spec = null;        // template spec from backend
    this.state = null;       // current personalization state
    this.variantMap = {};
    this.priceMap = null;    // variant id -> price in cents (from /products/<handle>.js)
    this.fallbackVariant = root.getAttribute("data-fallback-variant") || "";
    this.promo = readPromo(root);  // { percent, label } once, or null

    this.selectedDate = null; // { month, day, key }
    this.nasaImage = null;    // HTMLImageElement of the NASA source
    this.imageCache = {};

    // DOM refs (set by buildUI)
    this.heroEl = null;
    this.workspaceEl = null;
    this.canvasEl = null;
    this.controlsEl = null;
    this.footerEl = null;
    this.errorEl = null;
    this.addBtn = null;
    this.loaderEl = null;
  }

  BirthdayEditor.prototype.init = function () {
    var self = this;
    this.buildUI();

    Promise.all([
      this.loadCatalog(),
      this.loadSpec()
    ]).then(function () {
      self.setHeroReady();
    }).catch(function (e) {
      console.error("[birthday-editor]", e);
      self.showStatus("Could not load editor data: " + e.message);
    });

    // Prices are cosmetic (footer display) — load them independently so a
    // failure (e.g. local test harness with no storefront) never blocks boot.
    this.loadPrices().catch(function () {});
    this.loadPromo().catch(function () {});
  };

  /* The mount element's promo attributes are a hand-maintained fallback; the
     backend can read the real automatic discounts. Only overwrite when it
     actually answered, so a lookup failure leaves the theme's value alone. */
  BirthdayEditor.prototype.loadPromo = function () {
    var self = this;
    return fetch(this.proxyBase + "/promo?handle=" + encodeURIComponent(this.handle))
      .then(function (r) {
        if (!r.ok) throw new Error("promo endpoint returned " + r.status);
        return r.json();
      })
      .then(function (res) {
        if (!res || res.ok !== true) return;
        self.promo = res.promo
          ? { percent: res.promo.percent, label: res.promo.label || "" }
          : null;
        self.updateFooter();
      });
  };

  /* ── Data loading ──────────────────────────────────────────────────────── */
  BirthdayEditor.prototype.loadCatalog = function () {
    var self = this;
    return fetch(this.proxyBase + "/birthday-catalog")
      .then(function (r) {
        if (!r.ok) throw new Error("Catalog endpoint returned " + r.status);
        return r.json();
      })
      .then(function (data) { self.catalog = data; });
  };

  BirthdayEditor.prototype.loadSpec = function () {
    var self = this;
    return fetch(this.proxyBase + "/spec?handle=" + encodeURIComponent(this.handle))
      .then(function (r) {
        if (!r.ok) throw new Error("Spec endpoint returned " + r.status);
        return r.json();
      })
      .then(function (spec) {
        self.spec = spec;
        if (spec.variant_map) self.variantMap = spec.variant_map;
        self.state = Object.assign(
          { size: spec.default_size || spec.size_ids[0], style: spec.default_style || "Print Only" },
          spec.defaults,
          spec.text_defaults || {}
        );
        return loadCustomFonts(spec.options.fonts, self.proxyBase);
      });
  };

  BirthdayEditor.prototype.loadPrices = function () {
    var self = this;
    return fetch("/products/" + encodeURIComponent(this.handle) + ".js")
      .then(function (r) {
        if (!r.ok) throw new Error("product endpoint returned " + r.status);
        return r.json();
      })
      .then(function (product) {
        var map = {};
        (product.variants || []).forEach(function (v) { map[String(v.id)] = v.price; });
        self.priceMap = map;
        self.updateFooter();
      });
  };

  /* ── UI construction ───────────────────────────────────────────────────── */
  BirthdayEditor.prototype.buildUI = function () {
    this.root.innerHTML = "";
    this._hideHero = this.root.hasAttribute("data-bday-hide-hero");
    this._externalLaunch = this.root.style.display === "none";
    this.buildHero();
    this.buildWorkspace();
    this.buildFooter();
    if (this._hideHero) this.heroEl.style.display = "none";
  };

  BirthdayEditor.prototype.buildHero = function () {
    var self = this;
    var hero = el("div", "bday-hero");
    hero.innerHTML =
      '<h1 class="bday-hero__title">What Did Hubble See on Your Birthday?</h1>' +
      '<p class="bday-hero__subtitle">Every day, the Hubble Space Telescope captures a breathtaking view of the cosmos. Discover the image NASA released on your birthday and turn it into a personalised print.</p>' +
      '<div class="bday-hero__form">' +
        '<select data-bday-month aria-label="Month"></select>' +
        '<select data-bday-day aria-label="Day"></select>' +
        '<button class="bday-hero__go" data-bday-go disabled>Find My Image</button>' +
      '</div>';
    this.heroEl = hero;
    this.root.appendChild(hero);

    var monthSel = $(hero, "[data-bday-month]");
    var daySel = $(hero, "[data-bday-day]");
    var goBtn = $(hero, "[data-bday-go]");

    var mo = el("option"); mo.value = ""; mo.textContent = "Month"; mo.disabled = true; mo.selected = true;
    monthSel.appendChild(mo);
    MONTHS.forEach(function (name, i) {
      var o = el("option"); o.value = String(i + 1); o.textContent = name;
      monthSel.appendChild(o);
    });

    var dayPlaceholder = el("option"); dayPlaceholder.value = ""; dayPlaceholder.textContent = "Day"; dayPlaceholder.disabled = true; dayPlaceholder.selected = true;
    daySel.appendChild(dayPlaceholder);
    for (var d = 1; d <= 31; d++) {
      var o = el("option"); o.value = String(d); o.textContent = String(d);
      daySel.appendChild(o);
    }

    monthSel.addEventListener("change", function () { self.updateDayOptions(monthSel, daySel); self.checkGoReady(monthSel, daySel, goBtn); });
    daySel.addEventListener("change", function () { self.checkGoReady(monthSel, daySel, goBtn); });
    goBtn.addEventListener("click", function () { self.onDateSelected(parseInt(monthSel.value), parseInt(daySel.value)); });
  };

  BirthdayEditor.prototype.updateDayOptions = function (monthSel, daySel) {
    var m = parseInt(monthSel.value);
    if (!m) return;
    var max = DAYS_IN_MONTH[m - 1];
    var opts = daySel.querySelectorAll("option");
    opts.forEach(function (o) {
      if (o.value === "") return;
      var v = parseInt(o.value);
      o.hidden = v > max;
      o.disabled = v > max;
    });
    if (parseInt(daySel.value) > max) daySel.value = "";
  };

  BirthdayEditor.prototype.checkGoReady = function (monthSel, daySel, goBtn) {
    goBtn.disabled = !monthSel.value || !daySel.value;
  };

  BirthdayEditor.prototype.setHeroReady = function () {
    this._ready = true;
    var goBtn = $(this.heroEl, "[data-bday-go]");
    var monthSel = $(this.heroEl, "[data-bday-month]");
    var daySel = $(this.heroEl, "[data-bday-day]");
    this.checkGoReady(monthSel, daySel, goBtn);

    if (this._pendingDate) {
      var pd = this._pendingDate;
      this._pendingDate = null;
      this.onDateSelected(pd.month, pd.day, pd.imageFile);
    }
  };

  BirthdayEditor.prototype.buildWorkspace = function () {
    var ws = el("div", "bday-workspace");
    ws.hidden = true;

    // Preview pane
    var preview = el("div", "bday-preview");
    // Frame effect renders on a wrapper div (borders/padding/gradients) so the
    // canvas itself stays a clean print preview.
    var frameWrap = el("div", "bday-frame");
    frameWrap.setAttribute("data-bday-frame", "");
    var canvas = document.createElement("canvas");
    canvas.width = 600; canvas.height = 750;
    canvas.setAttribute("data-bday-canvas", "");
    // Tap the heading/subheading on the poster to jump to its text field
    var self = this;
    canvas.addEventListener("click", function (e) { self.onCanvasClick(e); });
    frameWrap.appendChild(canvas);
    preview.appendChild(frameWrap);

    var loader = el("div", "bday-preview__loader");
    loader.hidden = true;
    loader.innerHTML = '<div class="bday-preview__spinner"></div><span class="bday-preview__loader-text">Loading…</span>';
    preview.appendChild(loader);

    var story = el("div", "bday-story");
    story.innerHTML =
      '<div class="bday-story__inner">' +
        '<h2 class="bday-story__title" data-bday-img-title></h2>' +
        '<p class="bday-story__caption" data-bday-caption></p>' +
        // No link out to NASA's page for the image. The credit stays (it is owed,
        // and it is part of what makes the print feel real), but pointing at the
        // source invites the shopper to leave and download the original instead
        // of buying the finished, personalised print.
        '<div class="bday-story__meta">' +
          '<span class="bday-story__credit" data-bday-img-credit></span>' +
        '</div>' +
      '</div>';
    preview.appendChild(story);

    ws.appendChild(preview);

    // Controls pane
    var controls = el("div", "bday-controls");
    controls.setAttribute("data-bday-controls", "");
    ws.appendChild(controls);

    this.workspaceEl = ws;
    this.canvasEl = canvas;
    this.frameEl = frameWrap;
    this.controlsEl = controls;
    this.loaderEl = loader;
    this.root.appendChild(ws);
  };

  BirthdayEditor.prototype.buildFooter = function () {
    var self = this;
    var footer = el("div", "bday-footer");
    footer.hidden = true;
    footer.innerHTML =
      '<div class="bday-footer__info">' +
        '<span class="bday-footer__title" data-bday-footer-title></span>' +
        '<span class="bday-footer__meta" data-bday-footer-meta></span>' +
        '<span class="bday-footer__promo" data-bday-footer-promo hidden></span>' +
      '</div>' +
      '<span class="bday-error" data-bday-error></span>' +
      '<span class="bday-footer__price" data-bday-footer-price></span>' +
      '<div class="bday-footer__actions">' +
        '<button class="bday-share-btn" data-bday-share title="Share your birthday print">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>' +
          ' Share' +
        '</button>' +
        '<button class="bday-atc" data-bday-add disabled>Add to cart</button>' +
      '</div>';
    this.footerEl = footer;
    this.errorEl = $(footer, "[data-bday-error]");
    this.addBtn = $(footer, "[data-bday-add]");
    this.addBtn.addEventListener("click", function () { self.addToCart(); });
    $(footer, "[data-bday-share]").addEventListener("click", function () { self.shareCard(); });
    this.root.appendChild(footer);
    this.trackKeyboard();
  };

  /* ── Keep the footer above the on-screen keyboard ─────────────────────── */
  BirthdayEditor.prototype.trackKeyboard = function () {
    var vv = window.visualViewport;
    if (!vv || !this.footerEl) return;
    var footer = this.footerEl;
    var frame = 0;

    function sync() {
      frame = 0;
      // iOS keeps the layout viewport at full height when the keyboard opens,
      // so a bottom-anchored fixed element ends up behind it. The visual
      // viewport does shrink, and the gap between the two is exactly how much
      // of the footer is covered. Android resizes the layout viewport instead,
      // where this comes out at zero and nothing moves.
      var covered = window.innerHeight - vv.height - vv.offsetTop;
      footer.style.transform = covered > 1
        ? "translateY(" + -Math.round(covered) + "px)"
        : "";
    }

    function queue() {
      if (!frame) frame = requestAnimationFrame(sync);
    }

    vv.addEventListener("resize", queue);
    vv.addEventListener("scroll", queue);
    sync();
  };

  /* ── Full-screen overlay mode (launched from a landing page) ──────────── */
  BirthdayEditor.prototype.openOverlay = function () {
    if (!this._externalLaunch) return;
    this.root.style.display = "";
    this.root.classList.add("bday-editor--overlay");
    document.body.classList.add("bday-editor-open");
    this.root.scrollTop = 0;

    if (!this._closeBtn) {
      var self = this;
      var btn = el("button", "bday-editor__close");
      btn.setAttribute("aria-label", "Close editor");
      btn.innerHTML = "&times;";
      btn.addEventListener("click", function () { self.closeOverlay(); });
      this.root.appendChild(btn);
      this._closeBtn = btn;
    }
  };

  BirthdayEditor.prototype.closeOverlay = function () {
    this.root.classList.remove("bday-editor--overlay");
    document.body.classList.remove("bday-editor-open");
    this.root.style.display = "none";
  };

  /* ── Date selection handler ────────────────────────────────────────────── */
  BirthdayEditor.prototype.onDateSelected = function (month, day, imageFile) {
    if (!this._ready) {
      this._pendingDate = { month: month, day: day, imageFile: imageFile };
      this.openOverlay();
      return;
    }

    var key = dateKey(month, day);
    var entry = this.catalog[key];
    if (!entry || !entry.images || !entry.images.length) {
      showToast("No image found for this date");
      return;
    }

    this.openOverlay();

    var startIndex = 0;
    if (imageFile) {
      for (var i = 0; i < entry.images.length; i++) {
        if (entry.images[i].image === imageFile) { startIndex = i; break; }
      }
    }

    this.selectedDate = { month: month, day: day, key: key };
    this.selectedImageIndex = startIndex;

    this.heroEl.style.display = "none";
    this.workspaceEl.hidden = false;
    this.footerEl.hidden = false;
    this.addBtn.disabled = false;

    this.applySelectedImage(startIndex);
    this.buildControls();
  };

  BirthdayEditor.prototype.applySelectedImage = function (index) {
    var entry = this.catalog[this.selectedDate.key];
    var images = entry.images;
    if (index < 0 || index >= images.length) return;
    this.selectedImageIndex = index;
    var img = images[index];

    this.applyDefaultText();

    $(this.root, "[data-bday-img-title]").textContent = img.title;
    $(this.root, "[data-bday-img-credit]").textContent = "NASA / ESA / STScI" + (img.year ? " \u00B7 " + img.year : "");
    $(this.root, "[data-bday-caption]").textContent = img.caption;

    this.loadNasaImage(img);
    this.updateImagePicker();
    this.refreshTextInputs();
  };

  /* The heading and subheading we fill in on the shopper's behalf for the
     current date and image.

     The catalogue title has to satisfy the same limit their own typing does. It
     didn't: 129 of 1811 titles run past the old 30-character cap, which left 6%
     of dates opening in a state that refused to add to cart until the shopper
     noticed and edited text they never wrote. */
  BirthdayEditor.prototype.applyDefaultText = function () {
    var entry = this.catalog[this.selectedDate.key];
    var img = entry.images[this.selectedImageIndex];
    if (!img) return;
    var limits = this.spec.limits || {};
    this.state.heading = clampText(img.title, limits.heading_max_chars);
    this.state.subheading = clampText(
      MONTHS[this.selectedDate.month - 1] + " " + this.selectedDate.day +
      " \u00B7 NASA \u00B7 HUBBLE SPACE TELESCOPE",
      limits.subheading_max_chars);
  };

  /* Put the design back to how the date first presented itself.
     Deliberately keeps the date, the chosen image, and the size and style: those
     are the shopper's product decisions, and silently changing the size would
     change the price under them. Everything else is rebuilt from the spec
     defaults rather than patched, so a custom colour hex is dropped rather than
     lingering behind a named swatch. */
  BirthdayEditor.prototype.resetDesign = function () {
    if (!this.state || !this.spec) return;
    var before = Object.assign({}, this.state);
    var beforePlacements = this._placementState;

    this.state = Object.assign({},
      this.spec.defaults,
      this.spec.text_defaults || {},
      { size: before.size, style: before.style });
    // Per-placement memory describes the design we just discarded.
    this._placementState = null;

    this.applyDefaultText();
    this.applyDesignState();

    var self = this;
    showToast("Design reset", "Undo", function () {
      // `before` already carries whatever they had typed, so this must not go
      // back through the prefill or it would overwrite their own heading.
      self.state = before;
      self._placementState = beforePlacements;
      self.applyDesignState();
    });
  };

  /* Republish the whole of state to the UI. Needed after a wholesale swap, where
     set() isn't enough: the placement may differ, and the controls it builds
     differ with it. */
  BirthdayEditor.prototype.applyDesignState = function () {
    this.applyFrame();
    this.buildControls();
    this.render();
    this.updateFooter();
  };

  BirthdayEditor.prototype.refreshTextInputs = function () {
    var hInp = $(this.controlsEl, "[data-bday-heading-input]");
    var sInp = $(this.controlsEl, "[data-bday-subheading-input]");
    if (hInp) hInp.value = this.state.heading || "";
    if (sInp) sInp.value = this.state.subheading || "";
  };

  BirthdayEditor.prototype.updateImagePicker = function () {
    var thumbs = $$(this.controlsEl, "[data-bday-thumb]");
    var idx = this.selectedImageIndex;
    thumbs.forEach(function (t) {
      t.setAttribute("aria-pressed", String(Number(t.getAttribute("data-bday-thumb")) === idx));
    });
  };

  BirthdayEditor.prototype.loadNasaImage = function (imgEntry) {
    var self = this;
    var baseUrl = (this.spec && this.spec.birthday && this.spec.birthday.image_base_url)
      || "https://science.nasa.gov/specials/apps/what-did-hubble-see-on-your-birthday/images/";
    /* Prefer our own web-res copy: NASA's original is ~577 KB where the canvas
       needs a quarter of that, and it was the last third-party request standing
       between picking a date and seeing the poster. NASA remains the fallback. */
    var webUrl = this.proxyBase + "/birthday-web/" + encodeURIComponent(imgEntry.image);
    var originalUrl = baseUrl + imgEntry.image;

    self.showLoader("Loading NASA image\u2026");
    if (this.imageCache[imgEntry.image]) {
      this.nasaImage = this.imageCache[imgEntry.image];
      this.render();
      return;
    }

    var img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      self.imageCache[imgEntry.image] = img;
      self.nasaImage = img;
      self.render();
    };
    var triedOriginal = false;
    img.onerror = function () {
      if (!triedOriginal) {
        triedOriginal = true;
        img.src = originalUrl;
        return;
      }
      self.hideLoader();
      self.showStatus("Could not load NASA image");
    };
    img.src = webUrl;
  };

  /* ── Controls builder ──────────────────────────────────────────────────── */
  BirthdayEditor.prototype.buildControls = function () {
    var self = this, spec = this.spec, opts = spec.options, s = this.state;
    this.controlsEl.innerHTML = "";

    function field(labelText) {
      var f = el("div", "bday-field");
      var l = el("label"); l.textContent = labelText; f.appendChild(l);
      return f;
    }
    function textInput(key, max) {
      var inp = el("input"); inp.type = "text"; inp.value = s[key] || ""; if (max) inp.maxLength = max;
      if (key === "heading") inp.setAttribute("data-bday-heading-input", "");
      if (key === "subheading") inp.setAttribute("data-bday-subheading-input", "");
      inp.addEventListener("input", function () { self.set(key, inp.value); });
      return inp;
    }
    function selectField(key, items, labelKey, valueKey) {
      var sel = el("select");
      items.forEach(function (it) {
        var o = el("option"); o.value = it[valueKey]; o.textContent = it[labelKey];
        if (it[valueKey] === s[key]) o.selected = true;
        sel.appendChild(o);
      });
      sel.addEventListener("change", function () { self.set(key, sel.value); });
      return sel;
    }
    function swatches(idKey, customKey, colors) {
      var wrap = el("div", "bday-swatches");
      colors.forEach(function (c) {
        var b = el("button", "bday-swatch");
        b.type = "button";
        b.style.background = c.hex; b.title = c.label;
        b.setAttribute("aria-pressed", String(!s[customKey] && s[idKey] === c.id));
        b.addEventListener("click", function () {
          self.state[customKey] = null;
          self.set(idKey, c.id);
          wrap.querySelectorAll(".bday-swatch").forEach(function (x) { x.setAttribute("aria-pressed", "false"); });
          b.setAttribute("aria-pressed", "true");
          preview.style.background = c.hex;
        });
        wrap.appendChild(b);
      });

      var pickerWrap = el("div", "bday-picker-btn-wrap");
      var pickerBtn = el("button", "bday-picker-btn");
      pickerBtn.type = "button"; pickerBtn.title = "Pick a custom color";
      var preview = el("span", "bday-picker-btn__preview");
      preview.style.background = resolveColor(s, idKey, customKey, colors);
      var lbl = el("span"); lbl.textContent = "Custom"; lbl.style.fontSize = "11px";
      pickerBtn.appendChild(preview); pickerBtn.appendChild(lbl);

      var picker = el("input"); picker.type = "color"; picker.className = "bday-color-picker";
      picker.value = resolveColor(s, idKey, customKey, colors);
      picker.addEventListener("input", function () {
        self.state[customKey] = picker.value.toUpperCase();
        preview.style.background = picker.value;
        wrap.querySelectorAll(".bday-swatch").forEach(function (x) { x.setAttribute("aria-pressed", "false"); });
        self.render();
      });
      pickerBtn.addEventListener("click", function () { picker.click(); });
      pickerWrap.appendChild(pickerBtn); pickerWrap.appendChild(picker);
      wrap.appendChild(pickerWrap);
      return wrap;
    }
    function fontSizeRange(key, labelText) {
      var fsLim = (spec.limits && spec.limits[key]) || (spec.limits && spec.limits.font_size_px) || { min: 60, max: 360 };
      // Use != null / isNaN (not ||) so a valid 0 isn't snapped back to min.
      var min = fsLim.min != null ? fsLim.min : 60;
      var max = fsLim.max != null ? fsLim.max : 360;
      var raw = s[key];
      if (raw == null) raw = (spec.defaults && spec.defaults[key] != null) ? spec.defaults[key] : min;
      var num = Number(raw);
      if (isNaN(num)) num = min;
      var val = Math.max(min, Math.min(max, num));
      fsLim = { min: min, max: max };
      s[key] = val;
      var f = el("div", "bday-field bday-field--range");
      var l = el("label"); l.textContent = labelText;
      var valSpan = el("span", "bday-range-val"); valSpan.textContent = val + " px";
      l.appendChild(valSpan); f.appendChild(l);
      var inp = el("input"); inp.type = "range"; inp.className = "bday-range";
      inp.min = String(fsLim.min); inp.max = String(fsLim.max); inp.step = "1"; inp.value = String(val);
      inp.addEventListener("input", function () { valSpan.textContent = inp.value + " px"; self.set(key, parseInt(inp.value, 10)); });
      f.appendChild(inp);
      return f;
    }
    function trackingRange(key, labelText) {
      var trLim = (spec.limits && spec.limits[key]) || { min: 0, max: 0.15, step: 0.005 };
      // A valid 0 (no tracking) must survive — don't use `|| min`.
      var tmin = trLim.min != null ? trLim.min : 0;
      var tmax = trLim.max != null ? trLim.max : 0.15;
      var raw = s[key];
      if (raw == null) raw = (spec.defaults && spec.defaults[key] != null) ? spec.defaults[key] : tmin;
      var num = Number(raw);
      if (isNaN(num)) num = tmin;
      var val = Math.max(tmin, Math.min(tmax, num));
      trLim = { min: tmin, max: tmax, step: trLim.step };
      s[key] = val;
      var f = el("div", "bday-field bday-field--range");
      var l = el("label"); l.textContent = labelText;
      var valSpan = el("span", "bday-range-val"); valSpan.textContent = Math.round(val * 1000) / 10 + "%";
      l.appendChild(valSpan); f.appendChild(l);
      var inp = el("input"); inp.type = "range"; inp.className = "bday-range";
      inp.min = String(trLim.min); inp.max = String(trLim.max); inp.step = String(trLim.step || 0.005); inp.value = String(val);
      inp.addEventListener("input", function () { var n = parseFloat(inp.value); valSpan.textContent = Math.round(n * 1000) / 10 + "%"; self.set(key, n); });
      f.appendChild(inp);
      return f;
    }
    function section(title, action) {
      var sec = el("div", "bday-section");
      var h = el("div", "bday-section__title"); h.textContent = title;
      if (action) { h.classList.add("bday-section__title--split"); h.appendChild(action); }
      sec.appendChild(h);
      return sec;
    }

    // ── Inline date picker
    var secDate = section("Your Birthday");
    var dateRow = el("div", "bday-row");
    var fMonth = field("Month");
    var monthPick = el("select");
    MONTHS.forEach(function (m, i) {
      var o = el("option"); o.value = String(i + 1); o.textContent = m;
      if (i + 1 === self.selectedDate.month) o.selected = true;
      monthPick.appendChild(o);
    });
    fMonth.appendChild(monthPick);
    dateRow.appendChild(fMonth);

    var fDay = field("Day");
    var dayPick = el("select");
    var curMonth = self.selectedDate.month;
    var maxDay = DAYS_IN_MONTH[curMonth - 1];
    for (var dd = 1; dd <= maxDay; dd++) {
      var o = el("option"); o.value = String(dd); o.textContent = String(dd);
      if (dd === self.selectedDate.day) o.selected = true;
      dayPick.appendChild(o);
    }
    fDay.appendChild(dayPick);
    dateRow.appendChild(fDay);

    function onDateChange() {
      var m = parseInt(monthPick.value);
      var maxD = DAYS_IN_MONTH[m - 1];
      var d = Math.min(parseInt(dayPick.value) || 1, maxD);
      self.onDateSelected(m, d);
    }
    monthPick.addEventListener("change", function () {
      var m = parseInt(monthPick.value);
      var maxD = DAYS_IN_MONTH[m - 1];
      var prevDay = parseInt(dayPick.value) || 1;
      dayPick.innerHTML = "";
      for (var dd = 1; dd <= maxD; dd++) {
        var o = el("option"); o.value = String(dd); o.textContent = String(dd);
        if (dd === Math.min(prevDay, maxD)) o.selected = true;
        dayPick.appendChild(o);
      }
      onDateChange();
    });
    dayPick.addEventListener("change", onDateChange);

    secDate.appendChild(dateRow);
    this.controlsEl.appendChild(secDate);

    // ── Image picker (thumbnails for multiple images per day)
    var dateEntry = this.catalog[this.selectedDate.key];
    if (dateEntry && dateEntry.images && dateEntry.images.length > 1) {
      var secImg = section("Choose Your Image");
      var thumbGrid = el("div", "bday-thumb-grid");
      dateEntry.images.forEach(function (imgData, idx) {
        var thumbBaseUrl = (spec.birthday && spec.birthday.image_base_url)
          || "https://science.nasa.gov/specials/apps/what-did-hubble-see-on-your-birthday/images/";
        var btn = el("button", "bday-thumb");
        btn.type = "button";
        btn.setAttribute("data-bday-thumb", String(idx));
        btn.setAttribute("aria-pressed", String(idx === self.selectedImageIndex));
        btn.title = imgData.title + " (" + imgData.year + ")";
        var thumbImg = el("img");
        /* These squares render at ~105px, so pulling NASA's originals here cost
           about 3.4 MB per date change and left the customer waiting well over
           ten seconds on mobile data. Our pre-built 240px crops are ~9 KB. The
           original stays as an onerror fallback so a date whose thumbnail is
           missing still shows something. */
        var fullUrl = thumbBaseUrl + imgData.image;
        thumbImg.src = self.proxyBase + "/birthday-thumb/" +
          encodeURIComponent(imgData.image);
        thumbImg.addEventListener("error", function () {
          if (this.getAttribute("data-fellback")) return;
          this.setAttribute("data-fellback", "1");
          this.src = fullUrl;
        });
        thumbImg.alt = imgData.title;
        thumbImg.loading = "lazy";
        thumbImg.decoding = "async";
        btn.appendChild(thumbImg);
        var thumbLabel = el("span", "bday-thumb__label");
        thumbLabel.textContent = imgData.title;
        btn.appendChild(thumbLabel);
        btn.addEventListener("click", function () { self.applySelectedImage(idx); });
        thumbGrid.appendChild(btn);
      });
      secImg.appendChild(thumbGrid);
      this.controlsEl.appendChild(secImg);
    }

    // ── Size + Style on the top row (matches the main personalizer layout)
    var sizeIds = spec.size_ids || [];
    var sizeItems = sizeIds.map(function (id) {
      var info = (spec.sizes && spec.sizes[id]) || {};
      return { id: id, label: info.label || id };
    });
    var rowTop = el("div", "bday-row bday-row--tight");
    var fSize = field("Size");
    var sizeSel = el("select");
    sizeItems.forEach(function (it) {
      var o = el("option"); o.value = it.id; o.textContent = it.label;
      if (it.id === s.size) o.selected = true;
      sizeSel.appendChild(o);
    });
    // setSize (not set) so the style stays valid and the Style select rebuilds
    sizeSel.addEventListener("change", function () { self.setSize(sizeSel.value); });
    fSize.appendChild(sizeSel);
    rowTop.appendChild(fSize);
    var styleItems = this.getStylesForSize(s.size || sizeIds[0]);
    if (styleItems.length > 1) {
      var fStyle = field("Style");
      var styleSel = el("select");
      styleItems.forEach(function (st) {
        var o = el("option"); o.value = st; o.textContent = st;
        if (st === s.style) o.selected = true;
        styleSel.appendChild(o);
      });
      styleSel.addEventListener("change", function () { self.set("style", styleSel.value); });
      fStyle.appendChild(styleSel);
      rowTop.appendChild(fStyle);
    }
    this.controlsEl.appendChild(rowTop);

    // ── Design Options: Text Layout, then Shadow/Matte toggles two-up with
    // matte swatches stacked under the matte toggle (mirrors personalizer.js).
    /* Reset lives on the Design Options heading because that is what it undoes:
       the type, colour and layout choices below it, not the date or the size the
       shopper is being charged for. It stays enabled at all times rather than
       tracking whether anything has changed, since slider and swatch edits don't
       rebuild the controls and a disabled state would go stale. */
    var resetBtn = el("button", "bday-reset");
    resetBtn.type = "button";
    resetBtn.textContent = "Reset";
    resetBtn.title = "Undo your design changes and start from the original layout";
    resetBtn.addEventListener("click", function () { self.resetDesign(); });

    var secDesign = section("Design Options", resetBtn);
    var designRow = el("div", "bday-row bday-row--tight");
    if (spec.placements && spec.placements.length > 1) {
      var placementLabels = { caption: "Caption", bottom: "Overlay (Bottom)", top: "Overlay (Top)", none: "None (Image Only)" };
      var fPl = field("Text Layout");
      var plSel = el("select");
      spec.placements.forEach(function (p) {
        var o = el("option"); o.value = p; o.textContent = placementLabels[p] || p;
        if (p === s.placement) o.selected = true;
        plSel.appendChild(o);
      });
      plSel.addEventListener("change", function () {
        var newPl = plSel.value;
        var oldPl = s.placement || spec.default_placement;
        var SAVE_KEYS = ["heading_font","subheading_font","heading_color","subheading_color",
          "heading_custom_hex","subheading_custom_hex","heading_tracking","subheading_tracking",
          "heading_font_size_px","subheading_font_size_px","scrim","caption_gap_px"];

        // Save current placement's customizations
        if (!self._placementState) self._placementState = {};
        var saved = {};
        SAVE_KEYS.forEach(function (k) { saved[k] = s[k]; });
        self._placementState[oldPl] = saved;

        // Restore saved state for new placement, or apply defaults
        if (self._placementState[newPl]) {
          var prev = self._placementState[newPl];
          SAVE_KEYS.forEach(function (k) { if (prev[k] !== undefined) s[k] = prev[k]; });
        } else if (newPl === "caption") {
          s.heading_font = "intervogue-reg";
          s.subheading_font = "intervogue-light";
          s.heading_color = "charcoal";
          s.subheading_color = "charcoal";
          s.heading_tracking = 0.35;
          s.subheading_tracking = 0.28;
          s.heading_font_size_px = 92;
          s.subheading_font_size_px = 54;
          s.caption_gap_px = (spec.defaults && spec.defaults.caption_gap_px != null)
            ? spec.defaults.caption_gap_px : 45;
          s.scrim = false;
        } else if (newPl !== "none") {
          s.heading_color = "white";
          s.subheading_color = "white";
          s.heading_tracking = 0.012;
          s.subheading_tracking = 0.12;
          s.heading_font_size_px = 132;
          s.subheading_font_size_px = 78;
          s.heading_font = "kiona-bold";
          s.subheading_font = "bourton-line-bold";
          s.scrim = true;
        }
        self.set("placement", newPl);
        self.buildControls();
      });
      fPl.appendChild(plSel);
      designRow.appendChild(fPl);
    }
    if (designRow.children.length) secDesign.appendChild(designRow);

    var isCaption = (s.placement || spec.default_placement) === "caption";
    var togRow = el("div", "bday-row bday-row--tight bday-row--toggles");
    if (!isCaption) {
      var scrimTog = el("label", "bday-toggle");
      var scrimCb = el("input"); scrimCb.type = "checkbox"; scrimCb.checked = s.scrim !== false;
      scrimCb.addEventListener("change", function () { self.set("scrim", scrimCb.checked); });
      var scrimLbl = el("span"); scrimLbl.textContent = "Shadow gradient";
      scrimTog.appendChild(scrimCb); scrimTog.appendChild(scrimLbl);
      togRow.appendChild(scrimTog);
    }
    if (opts.matte && opts.matte.available) {
      var matteGroup = el("div", "bday-matte-group");
      var matteTog = el("label", "bday-toggle");
      var cb = el("input"); cb.type = "checkbox"; cb.checked = !!s.matte;
      var matteSw = el("div", "bday-swatches bday-swatches--inline");
      opts.matte.colors.forEach(function (c) {
        var b = el("button", "bday-swatch");
        b.type = "button"; b.style.background = c.hex; b.title = c.label;
        b.setAttribute("aria-pressed", String(s.matte_color === c.id));
        b.addEventListener("click", function () {
          self.set("matte_color", c.id);
          matteSw.querySelectorAll(".bday-swatch").forEach(function (x) { x.setAttribute("aria-pressed", "false"); });
          b.setAttribute("aria-pressed", "true");
        });
        matteSw.appendChild(b);
      });
      matteSw.style.display = s.matte ? "" : "none";
      cb.addEventListener("change", function () {
        self.set("matte", cb.checked);
        matteSw.style.display = cb.checked ? "" : "none";
      });
      var matteLbl = el("span"); matteLbl.textContent = "White border";
      matteTog.appendChild(cb); matteTog.appendChild(matteLbl);
      matteGroup.appendChild(matteTog);
      matteGroup.appendChild(matteSw);
      togRow.appendChild(matteGroup);
    }
    if (togRow.children.length) secDesign.appendChild(togRow);
    this.controlsEl.appendChild(secDesign);
    this.applyFrame();

    // ── Heading & Subheading (hidden when placement is "none")
    var isNone = (s.placement || spec.default_placement) === "none";
    if (!isNone) {
      var secH = section("Heading");
      var fHead = field("Text");
      fHead.appendChild(textInput("heading", spec.limits.heading_max_chars));
      secH.appendChild(fHead);
      var rowH = el("div", "bday-row");
      var fHFont = field("Font");
      fHFont.appendChild(selectField("heading_font", fontsForRole(opts.fonts, "heading"), "label", "id"));
      var fHCol = field("Color");
      fHCol.appendChild(swatches("heading_color", "heading_custom_hex", opts.heading_colors));
      rowH.appendChild(fHFont); rowH.appendChild(fHCol);
      secH.appendChild(rowH);
      // Paired sliders: Size + Letter spacing two-up keeps the panel short
      var rowHSliders = el("div", "bday-row bday-row--tight");
      rowHSliders.appendChild(fontSizeRange("heading_font_size_px", "Size"));
      rowHSliders.appendChild(trackingRange("heading_tracking", "Letter spacing"));
      secH.appendChild(rowHSliders);
      this.controlsEl.appendChild(secH);

      var secS = section("Subheading");
      var fSub = field("Text");
      fSub.appendChild(textInput("subheading", spec.limits.subheading_max_chars));
      secS.appendChild(fSub);
      var rowS = el("div", "bday-row");
      var fSFont = field("Font");
      fSFont.appendChild(selectField("subheading_font", fontsForRole(opts.fonts, "subheading"), "label", "id"));
      var fSCol = field("Color");
      fSCol.appendChild(swatches("subheading_color", "subheading_custom_hex", opts.subheading_colors));
      rowS.appendChild(fSFont); rowS.appendChild(fSCol);
      secS.appendChild(rowS);
      var rowSSliders = el("div", "bday-row bday-row--tight");
      rowSSliders.appendChild(fontSizeRange("subheading_font_size_px", "Size"));
      rowSSliders.appendChild(trackingRange("subheading_tracking", "Letter spacing"));
      secS.appendChild(rowSSliders);
      // Caption only: the other placements space their lines with the
      // typography gap, so this slider would have nothing to act on there.
      if ((s.placement || spec.default_placement) === "caption") {
        secS.appendChild(fontSizeRange("caption_gap_px", "Space above heading"));
      }
      this.controlsEl.appendChild(secS);
    }

    this.updateFooter();
  };

  var STYLE_ORDER = [
    "Print Only", "Black Frame Print", "White Frame Print", "Wood Frame Print",
    "Black Floating Frame Canvas", "Walnut Floating Frame Canvas", "White Floating Frame Canvas"
  ];

  BirthdayEditor.prototype.getStylesForSize = function (size) {
    if (!this.variantMap || !this.variantMap[size]) return ["Print Only"];
    if (typeof this.variantMap[size] === "object" && !Array.isArray(this.variantMap[size])) {
      var keys = Object.keys(this.variantMap[size]);
      keys.sort(function (a, b) {
        var ia = STYLE_ORDER.indexOf(a), ib = STYLE_ORDER.indexOf(b);
        if (ia < 0) ia = 999;
        if (ib < 0) ib = 999;
        return ia - ib;
      });
      return keys;
    }
    return ["Print Only"];
  };

  BirthdayEditor.prototype.setSize = function (size) {
    this.state.size = size;
    // Not every size offers every frame style (e.g. 5x7 is print-only), so keep
    // the selected style valid — prefer Black Frame Print, else the first option.
    var styles = this.getStylesForSize(size);
    if (styles.indexOf(this.state.style) < 0) {
      this.state.style = styles.indexOf("Black Frame Print") >= 0 ? "Black Frame Print" : styles[0];
    }
    if (this.errorEl) this.errorEl.textContent = "";
    this.buildControls();
    this.applyFrame();
    this.render();
    this.updateFooter();
  };

  BirthdayEditor.prototype.resetToHero = function () {
    this.heroEl.style.display = "";
    this.workspaceEl.hidden = true;
    this.footerEl.hidden = true;
  };

  /* ── State management ──────────────────────────────────────────────────── */
  BirthdayEditor.prototype.set = function (key, value) {
    this.state[key] = value;
    if (this.errorEl) this.errorEl.textContent = "";
    if (key === "style") this.applyFrame();
    this.render();
    this.updateFooter();
  };

  /* ── Tap-to-edit (ported from personalizer.js) ─────────────────────────── */
  BirthdayEditor.prototype.onCanvasClick = function (e) {
    if (!this.hitRegions) return;
    var canvas = this.canvasEl;
    var rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var x = (e.clientX - rect.left) * (canvas.width / rect.width);
    var y = (e.clientY - rect.top) * (canvas.height / rect.height);
    // Generous padding makes small (e.g. subheading) text easy to tap.
    var padX = canvas.width * 0.02, padY = canvas.height * 0.02;
    var roles = ["heading", "subheading"], hit = null;
    for (var i = 0; i < roles.length; i++) {
      var bx = this.hitRegions[roles[i]];
      if (bx && x >= bx.x - padX && x <= bx.x + bx.w + padX &&
              y >= bx.y - padY && y <= bx.y + bx.h + padY) { hit = roles[i]; break; }
    }
    if (hit) this.focusField(hit);
  };

  BirthdayEditor.prototype.focusField = function (role) {
    var sel = role === "heading" ? "[data-bday-heading-input]" : "[data-bday-subheading-input]";
    var inp = this.controlsEl && this.controlsEl.querySelector(sel);
    if (!inp) return;
    try { inp.scrollIntoView({ behavior: "smooth", block: "center" }); }
    catch (e) { inp.scrollIntoView(); }
    try { inp.focus({ preventScroll: true }); } catch (e) { inp.focus(); }
    try { inp.select(); } catch (e) { /* ignore */ }
    var field = inp.closest ? inp.closest(".bday-field") : null;
    if (field) {
      field.classList.remove("bday-field--flash");
      void field.offsetWidth;                 // restart the flash animation
      field.classList.add("bday-field--flash");
    }
  };

  // Decorative frame preview around the canvas, driven by the selected style.
  BirthdayEditor.prototype.applyFrame = function () {
    if (!this.frameEl) return;
    var fs = frameSpec(this.state && this.state.style);
    this.frameEl.setAttribute("data-frame-kind", fs.kind);
    this.frameEl.setAttribute("data-frame-tone", fs.tone);
  };

  /* Resolve the Shopify variant id for the current size/style selection.
     Shared by the footer price display and addToCart. */
  BirthdayEditor.prototype.currentVariantId = function () {
    if (!this.state) return null;
    var sizeKey = this.state.size || this.state.ratio;
    var styleKey = this.state.style || "Print Only";
    var sizeEntry = this.variantMap[sizeKey];
    if (sizeEntry && typeof sizeEntry === "object" && !Array.isArray(sizeEntry)) {
      return sizeEntry[styleKey] || sizeEntry["Print Only"] || Object.values(sizeEntry)[0] || null;
    }
    return sizeEntry || this.fallbackVariant || null;
  };

  BirthdayEditor.prototype.updateFooter = function () {
    if (!this.selectedDate || !this.state) return;
    var entry = this.catalog[this.selectedDate.key];
    if (!entry) return;

    var sizeInfo = this.spec.sizes[this.state.size];
    var sizeLabel = sizeInfo ? sizeInfo.label : this.state.size;
    var style = this.state.style || "Print Only";

    $(this.footerEl, "[data-bday-footer-title]").textContent = entry.title;
    $(this.footerEl, "[data-bday-footer-meta]").textContent = style !== "Print Only" ? style : "";

    // Size and price render together, in the same type style
    var priceEl = $(this.footerEl, "[data-bday-footer-price]");
    var variantId = this.currentVariantId();
    var cents = (this.priceMap && variantId != null) ? this.priceMap[String(variantId)] : undefined;
    priceEl.textContent = sizeLabel;
    if (typeof cents === "number") {
      var off = promoDiscount(this.promo, cents);
      priceEl.appendChild(document.createTextNode(" \u00B7 " + money(cents - off)));
      if (off > 0) {
        var was = el("span", "bday-footer__was");
        was.textContent = money(cents);
        priceEl.appendChild(was);
      }
    }
    priceEl.hidden = false;

    var note = $(this.footerEl, "[data-bday-footer-promo]");
    if (note) {
      note.hidden = !this.promo;
      if (this.promo) {
        note.textContent = Math.round(this.promo.percent) + "% off applied at checkout" +
          (this.promo.label ? " \u00B7 " + this.promo.label : "");
      }
    }
  };

  /* ── Rendering ─────────────────────────────────────────────────────────── */
  BirthdayEditor.prototype.showLoader = function (msg) {
    if (this.loaderEl) {
      $(this.loaderEl, ".bday-preview__loader-text").textContent = msg || "Loading\u2026";
      this.loaderEl.hidden = false;
    }
    var preview = this.canvasEl ? this.canvasEl.parentElement : null;
    if (preview) preview.classList.add("bday-preview--loading");
  };

  BirthdayEditor.prototype.hideLoader = function () {
    if (this.loaderEl) this.loaderEl.hidden = true;
    var preview = this.canvasEl ? this.canvasEl.parentElement : null;
    if (preview) preview.classList.remove("bday-preview--loading");
  };

  BirthdayEditor.prototype.showStatus = function (msg) {
    this.hideLoader();
    if (this.errorEl) this.errorEl.textContent = msg;
  };

  BirthdayEditor.prototype.render = function () {
    if (!this.nasaImage || !this.spec || !this.state) return;

    var spec = this.spec, s = this.state;
    var aspect = previewAspect(spec, s);
    var cw = 600, ch = Math.round(cw / aspect);
    this.canvasEl.width = cw;
    this.canvasEl.height = ch;
    var ctx = this.canvasEl.getContext("2d");

    this.paint(ctx, this.nasaImage, cw, ch);
    this.hideLoader();
  };

  BirthdayEditor.prototype.paint = function (ctx, img, W, H) {
    var spec = this.spec, s = this.state, opts = spec.options;
    var placement = s.placement || spec.default_placement;

    if (placement === "caption") {
      this.paintCaption(ctx, img, W, H);
      return;
    }

    if (placement === "none") {
      this.paintNone(ctx, img, W, H);
      return;
    }

    var layout = spec.layout[placement];
    var typo = scaledTypo(spec, H, s);
    var boxLeft = typo.boxX * W;
    var boxW = typo.boxW * W;

    var off = document.createElement("canvas"); off.width = W; off.height = H;
    var octx = off.getContext("2d");

    var imgAspect = img.width / img.height;
    var canvasAspect = W / H;
    var sx = 0, sy = 0, sw = img.width, sh = img.height;
    if (imgAspect > canvasAspect) {
      sw = img.height * canvasAspect;
      sx = (img.width - sw) / 2;
    } else {
      sh = img.width / canvasAspect;
      sy = (img.height - sh) / 2;
    }
    octx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);

    var scrim = layout.scrim;
    if (s.scrim !== false && scrim && scrim.enabled) {
      var rx = scrim.rect.x * W, ry = scrim.rect.y * H, rw = scrim.rect.w * W, rh = scrim.rect.h * H;
      var g = octx.createLinearGradient(0, ry, 0, ry + rh);
      var maxA = scrim.max_opacity != null ? scrim.max_opacity : 0.45;
      if (scrim.gradient === "vertical-flip") { g.addColorStop(0, "rgba(0,0,0," + maxA + ")"); g.addColorStop(1, "rgba(0,0,0,0)"); }
      else { g.addColorStop(0, "rgba(0,0,0,0)"); g.addColorStop(1, "rgba(0,0,0," + maxA + ")"); }
      octx.fillStyle = g; octx.fillRect(rx, ry, rw, rh);
    }

    var regions = {};
    function drawLine(role, text, fontId, hex, startSize, minSize, trackingFrac, align, uppercase, y) {
      if (!text) return 0;
      var content = uppercase ? text.toUpperCase() : text;
      var family = familyOf(opts.fonts, fontId);
      var wt = fontWeight(opts.fonts, fontId);
      var fit = fitText(octx, content, family, wt, boxW, startSize, minSize, trackingFrac);
      regions[role] = drawTracked(octx, content, family, wt, fit.size, fit.tracking, hex, align || "center", boxLeft, boxW, y);
      return fit.size * 1.15;
    }

    var hBox = layout.heading || {};
    var sBox = layout.subheading || {};
    var headHex = resolveColor(s, "heading_color", "heading_custom_hex", opts.heading_colors);
    var subHex = resolveColor(s, "subheading_color", "subheading_custom_hex", opts.subheading_colors);
    var headUc = fontUppercase(opts.fonts, s.heading_font, hBox.uppercase !== false);
    var subUc = fontUppercase(opts.fonts, s.subheading_font, sBox.uppercase !== false);

    if (placement === "top") {
      var y = typo.margin;
      if (s.heading) { y += drawLine("heading", s.heading, s.heading_font, headHex, typo.headingStart, typo.headingMin, typo.headingTracking, hBox.align, headUc, y) + typo.gap; }
      if (s.subheading) { drawLine("subheading", s.subheading, s.subheading_font, subHex, typo.subStart, typo.subMin, typo.subTracking, sBox.align, subUc, y); }
    } else {
      var ySub = H - typo.margin;
      if (s.subheading) {
        var subContent = subUc ? s.subheading.toUpperCase() : s.subheading;
        var subFamily = familyOf(opts.fonts, s.subheading_font);
        var subWt = fontWeight(opts.fonts, s.subheading_font);
        var subFit = fitText(octx, subContent, subFamily, subWt, boxW, typo.subStart, typo.subMin, typo.subTracking);
        ySub = H - typo.margin - subFit.size * 1.15;
        regions.subheading = drawTracked(octx, subContent, subFamily, subWt, subFit.size, subFit.tracking, subHex, sBox.align || "center", boxLeft, boxW, ySub);
      }
      if (s.heading) {
        var headContent = headUc ? s.heading.toUpperCase() : s.heading;
        var headFamily = familyOf(opts.fonts, s.heading_font);
        var headWt = fontWeight(opts.fonts, s.heading_font);
        var headFit = fitText(octx, headContent, headFamily, headWt, boxW, typo.headingStart, typo.headingMin, typo.headingTracking);
        var yHead = (s.subheading ? ySub : H - typo.margin) - headFit.size * 1.15 - (s.subheading ? typo.gap : 0);
        regions.heading = drawTracked(octx, headContent, headFamily, headWt, headFit.size, headFit.tracking, headHex, hBox.align || "center", boxLeft, boxW, yHead);
      }
    }

    ctx.clearRect(0, 0, W, H);
    var matteOn = s.matte && opts.matte;
    var b = 0;
    if (matteOn) {
      var bf = opts.matte.border_frac || 0.05;
      b = Math.round(Math.min(W, H) * bf);
      ctx.fillStyle = hexOf(opts.matte.colors, s.matte_color);
      ctx.fillRect(0, 0, W, H);
      ctx.drawImage(off, b, b, W - 2 * b, H - 2 * b);
    } else {
      ctx.drawImage(off, 0, 0);
    }

    // Hit-regions were recorded in offscreen space; the matte blit scales them
    // (mirrors personalizer.js toCanvasSpace) so taps land on the right block.
    function toCanvasSpace(box) {
      if (!box) return null;
      if (!matteOn) return box;
      var sxr = (W - 2 * b) / W, syr = (H - 2 * b) / H;
      return { x: b + box.x * sxr, y: b + box.y * syr, w: box.w * sxr, h: box.h * syr };
    }
    this.hitRegions = { heading: toCanvasSpace(regions.heading), subheading: toCanvasSpace(regions.subheading) };
  };

  BirthdayEditor.prototype.paintCaption = function (ctx, img, W, H) {
    var spec = this.spec, s = this.state, opts = spec.options;
    var cap = spec.layout.caption;
    var ref = (spec.layout.typography && spec.layout.typography.reference_height_px) || 5000;
    var scale = H / ref;
    var hasMatte = s.matte && opts.matte;

    ctx.clearRect(0, 0, W, H);

    if (hasMatte) {
      // Matte ON: white/colored border with image inset and caption text in the border zone
      var matteHex = hexOf(opts.matte.colors, s.matte_color);
      ctx.fillStyle = matteHex;
      ctx.fillRect(0, 0, W, H);

      var sideFrac = cap.image_side_frac || 0.035;
      var imgLeft = Math.round(sideFrac * W);
      var imgRight = W - imgLeft;
      var imgTop = Math.round((cap.image_top_frac || 0.042) * H);
      var imgBottom = Math.round((cap.image_bottom_frac || 0.895) * H);
      var imgZoneW = imgRight - imgLeft;
      var imgZoneH = imgBottom - imgTop;

      var imgAspect = img.width / img.height;
      var zoneAspect = imgZoneW / imgZoneH;
      var sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (imgAspect > zoneAspect) { sw = img.height * zoneAspect; sx = (img.width - sw) / 2; }
      else { sh = img.width / zoneAspect; sy = (img.height - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, imgLeft, imgTop, imgZoneW, imgZoneH);

      // Caption text in the border zone below the image
      var boxX = (cap.box_x_frac || 0.06) * W;
      var boxW = (cap.box_w_frac || 0.88) * W;
      var headFontId = s.heading_font || cap.title_font || "intervogue-reg";
      var subFontId = s.subheading_font || cap.subtitle_font || "intervogue-light";
      var headHex = resolveColor(s, "heading_color", "heading_custom_hex", opts.heading_colors);
      var subHex = resolveColor(s, "subheading_color", "subheading_custom_hex", opts.subheading_colors);
      var headTrack = s.heading_tracking != null ? s.heading_tracking : (cap.title_tracking || 0.35);
      var subTrack = s.subheading_tracking != null ? s.subheading_tracking : (cap.subtitle_tracking || 0.28);
      var headStart = Math.round(((s.heading_font_size_px) || cap.title_font_px || 108) * scale);
      var headMin = Math.round((cap.min_title_font_px || 60) * scale);
      var subStart = Math.round(((s.subheading_font_size_px) || cap.subtitle_font_px || 63) * scale);
      var subMin = Math.round((cap.min_subtitle_font_px || 36) * scale);
      var headUc = fontUppercase(opts.fonts, headFontId, cap.title_uppercase !== false);
      var subUc = fontUppercase(opts.fonts, subFontId, cap.subtitle_uppercase !== false);
      var titleY = Math.round((cap.title_y_frac || 0.925) * H);
      var subY = Math.round((cap.subtitle_y_frac || 0.955) * H)
        + Math.round(capGap(s, spec) * scale);

      this.hitRegions = { heading: null, subheading: null };
      if (s.heading) {
        var hContent = headUc ? s.heading.toUpperCase() : s.heading;
        var hFamily = familyOf(opts.fonts, headFontId);
        var hWt = fontWeight(opts.fonts, headFontId);
        var hFit = fitText(ctx, hContent, hFamily, hWt, boxW, headStart, headMin, headTrack);
        this.hitRegions.heading = drawTracked(ctx, hContent, hFamily, hWt, hFit.size, hFit.tracking, headHex, "center", boxX, boxW, titleY);
      }
      if (s.subheading) {
        var sContent = subUc ? s.subheading.toUpperCase() : s.subheading;
        var sFamily = familyOf(opts.fonts, subFontId);
        var sWt = fontWeight(opts.fonts, subFontId);
        var sFit = fitText(ctx, sContent, sFamily, sWt, boxW, subStart, subMin, subTrack);
        this.hitRegions.subheading = drawTracked(ctx, sContent, sFamily, sWt, sFit.size, sFit.tracking, subHex, "center", boxX, boxW, subY);
      }
    } else {
      // Matte OFF: full bleed image with a white banner at bottom for caption text
      var bannerFrac = cap.bleed_banner_frac || 0.105;
      var bannerH = Math.round(bannerFrac * H);
      var imgZoneH = H - bannerH;

      var imgAspect = img.width / img.height;
      var zoneAspect = W / imgZoneH;
      var sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (imgAspect > zoneAspect) { sw = img.height * zoneAspect; sx = (img.width - sw) / 2; }
      else { sh = img.width / zoneAspect; sy = (img.height - sh) / 2; }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, imgZoneH);

      // White banner (pure #FFFFFF — matches matte white)
      ctx.fillStyle = cap.bg_color || "#FFFFFF";
      ctx.fillRect(0, imgZoneH, W, bannerH);

      // Caption text in the banner
      var boxX = (cap.box_x_frac || 0.06) * W;
      var boxW = (cap.box_w_frac || 0.88) * W;
      var headFontId = s.heading_font || cap.title_font || "intervogue-reg";
      var subFontId = s.subheading_font || cap.subtitle_font || "intervogue-light";
      var headHex = resolveColor(s, "heading_color", "heading_custom_hex", opts.heading_colors);
      var subHex = resolveColor(s, "subheading_color", "subheading_custom_hex", opts.subheading_colors);
      var headTrack = s.heading_tracking != null ? s.heading_tracking : (cap.title_tracking || 0.35);
      var subTrack = s.subheading_tracking != null ? s.subheading_tracking : (cap.subtitle_tracking || 0.28);
      var headStart = Math.round(((s.heading_font_size_px) || cap.title_font_px || 108) * scale);
      var headMin = Math.round((cap.min_title_font_px || 60) * scale);
      var subStart = Math.round(((s.subheading_font_size_px) || cap.subtitle_font_px || 63) * scale);
      var subMin = Math.round((cap.min_subtitle_font_px || 36) * scale);
      var headUc = fontUppercase(opts.fonts, headFontId, cap.title_uppercase !== false);
      var subUc = fontUppercase(opts.fonts, subFontId, cap.subtitle_uppercase !== false);

      var bannerMid = imgZoneH + bannerH * (cap.bleed_title_banner_frac != null ? cap.bleed_title_banner_frac : 0.25);
      var bannerSubY = imgZoneH + bannerH * (cap.bleed_subtitle_banner_frac != null ? cap.bleed_subtitle_banner_frac : 0.50)
        + capGap(s, spec) * scale;

      this.hitRegions = { heading: null, subheading: null };
      if (s.heading) {
        var hContent = headUc ? s.heading.toUpperCase() : s.heading;
        var hFamily = familyOf(opts.fonts, headFontId);
        var hWt = fontWeight(opts.fonts, headFontId);
        var hFit = fitText(ctx, hContent, hFamily, hWt, boxW, headStart, headMin, headTrack);
        this.hitRegions.heading = drawTracked(ctx, hContent, hFamily, hWt, hFit.size, hFit.tracking, headHex, "center", boxX, boxW, bannerMid);
      }
      if (s.subheading) {
        var sContent = subUc ? s.subheading.toUpperCase() : s.subheading;
        var sFamily = familyOf(opts.fonts, subFontId);
        var sWt = fontWeight(opts.fonts, subFontId);
        var sFit = fitText(ctx, sContent, sFamily, sWt, boxW, subStart, subMin, subTrack);
        this.hitRegions.subheading = drawTracked(ctx, sContent, sFamily, sWt, sFit.size, sFit.tracking, subHex, "center", boxX, boxW, bannerSubY);
      }
    }
  };

  BirthdayEditor.prototype.paintNone = function (ctx, img, W, H) {
    var s = this.state, opts = this.spec.options;
    this.hitRegions = null;   // no text on the poster, nothing to tap
    ctx.clearRect(0, 0, W, H);

    var imgAspect = img.width / img.height;
    var canvasAspect = W / H;
    var sx = 0, sy = 0, sw = img.width, sh = img.height;
    if (imgAspect > canvasAspect) { sw = img.height * canvasAspect; sx = (img.width - sw) / 2; }
    else { sh = img.width / canvasAspect; sy = (img.height - sh) / 2; }

    if (s.matte && opts.matte) {
      var bf = opts.matte.border_frac || 0.05;
      var b = Math.round(Math.min(W, H) * bf);
      ctx.fillStyle = hexOf(opts.matte.colors, s.matte_color);
      ctx.fillRect(0, 0, W, H);
      var innerW = W - 2 * b, innerH = H - 2 * b;
      var innerAspect = innerW / innerH;
      var sx2 = 0, sy2 = 0, sw2 = img.width, sh2 = img.height;
      if (imgAspect > innerAspect) { sw2 = img.height * innerAspect; sx2 = (img.width - sw2) / 2; }
      else { sh2 = img.width / innerAspect; sy2 = (img.height - sh2) / 2; }
      ctx.drawImage(img, sx2, sy2, sw2, sh2, b, b, innerW, innerH);
    } else {
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
    }
  };

  /* ── Preview upload ────────────────────────────────────────────────────── */
  BirthdayEditor.prototype.uploadPreview = function () {
    var self = this;
    return new Promise(function (resolve, reject) {
      var dataUrl;
      try { dataUrl = self.canvasEl.toDataURL("image/jpeg", 0.88); }
      catch (e) { reject(new Error("Could not capture preview")); return; }
      fetch(self.proxyBase + "/preview/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: dataUrl })
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          if (!res.ok || !res.j.ok) throw new Error(res.j.error || "Preview upload failed");
          resolve(res.j.url || res.j.path);
        })
        .catch(reject);
    });
  };

  /* ── Share Card ─────────────────────────────────────────────────────────── */
  BirthdayEditor.prototype.generateShareCanvas = function (logo) {
    var CARD_W = 1080, CARD_H = 1350;
    var card = document.createElement("canvas");
    card.width = CARD_W; card.height = CARD_H;
    var ctx = card.getContext("2d");

    ctx.fillStyle = "#08080e";
    ctx.fillRect(0, 0, CARD_W, CARD_H);

    var grd = ctx.createRadialGradient(CARD_W / 2, CARD_H * 0.42, 50, CARD_W / 2, CARD_H * 0.42, CARD_W * 0.6);
    grd.addColorStop(0, "rgba(30,40,80,0.35)");
    grd.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, CARD_W, CARD_H);

    var tagline = "WHAT DID HUBBLE SEE ON YOUR BIRTHDAY?";
    ctx.font = "500 13px 'Assistant', 'Helvetica Neue', sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.textAlign = "center";
    ctx.letterSpacing = "4px";
    ctx.fillText(tagline, CARD_W / 2, 60);
    ctx.letterSpacing = "0px";

    var spec = this.spec, s = this.state;
    var ratio = s.size && spec.sizes[s.size] ? spec.sizes[s.size].ratio : spec.default_ratio;
    var printPx = spec.print[ratio] ? spec.print[ratio].px : [3600, 5400];
    var pAspect = printPx[0] / printPx[1];

    var maxPosterH = CARD_H * 0.58;
    var maxPosterW = CARD_W * 0.72;
    var posterW, posterH;
    if (pAspect > maxPosterW / maxPosterH) { posterW = maxPosterW; posterH = maxPosterW / pAspect; }
    else { posterH = maxPosterH; posterW = maxPosterH * pAspect; }
    posterW = Math.round(posterW); posterH = Math.round(posterH);

    var posterCanvas = document.createElement("canvas");
    posterCanvas.width = posterW; posterCanvas.height = posterH;
    this.paint(posterCanvas.getContext("2d"), this.nasaImage, posterW, posterH);

    var posterX = Math.round((CARD_W - posterW) / 2);
    var posterY = 100;

    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.55)"; ctx.shadowBlur = 40; ctx.shadowOffsetY = 12;
    ctx.fillStyle = "#000"; ctx.fillRect(posterX, posterY, posterW, posterH);
    ctx.restore();
    ctx.drawImage(posterCanvas, posterX, posterY, posterW, posterH);
    ctx.strokeStyle = "rgba(255,255,255,0.08)"; ctx.lineWidth = 1;
    ctx.strokeRect(posterX + 0.5, posterY + 0.5, posterW - 1, posterH - 1);

    var entry = this.catalog[this.selectedDate.key];
    var img = entry.images[this.selectedImageIndex];
    var monthLabel = MONTHS[this.selectedDate.month - 1].toUpperCase();
    var dateStr = monthLabel + " " + this.selectedDate.day;
    var textY = posterY + posterH + 50;

    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255,255,255,0.40)"; ctx.font = "400 14px 'Assistant', sans-serif";
    ctx.letterSpacing = "5px"; ctx.fillText("YOUR BIRTHDAY", CARD_W / 2, textY); ctx.letterSpacing = "0px";

    textY += 38;
    ctx.fillStyle = "#fff"; ctx.font = "600 36px 'Assistant', sans-serif";
    ctx.letterSpacing = "6px"; ctx.fillText(dateStr, CARD_W / 2, textY); ctx.letterSpacing = "0px";

    textY += 40;
    ctx.fillStyle = "#ffffff"; ctx.font = "600 22px 'Assistant', sans-serif";
    var titleText = img.title;
    if (ctx.measureText(titleText).width > CARD_W * 0.82) titleText = titleText.substring(0, 45) + "\u2026";
    ctx.fillText(titleText, CARD_W / 2, textY);

    if (img.caption) {
      textY += 36;
      ctx.fillStyle = "rgba(255,255,255,0.70)"; ctx.font = "300 18px 'Assistant', sans-serif";
      var desc = img.caption;
      var maxDescChars = 350;
      if (desc.length > maxDescChars) desc = desc.substring(0, maxDescChars - 3) + "\u2026";
      var words = desc.split(" "), lines = [], line = "";
      var lineW = CARD_W * 0.76;
      for (var i = 0; i < words.length; i++) {
        var test = line ? line + " " + words[i] : words[i];
        if (ctx.measureText(test).width > lineW) { lines.push(line); line = words[i]; } else { line = test; }
      }
      if (line) lines.push(line);
      var lineH = 28;
      var maxLines = 7;
      for (var j = 0; j < Math.min(lines.length, maxLines); j++) ctx.fillText(lines[j], CARD_W / 2, textY + j * lineH);
    }

    // Logo at bottom
    if (logo) {
      var logoH = 120;
      var logoW = Math.round(logoH * (logo.width / logo.height));
      ctx.globalAlpha = 0.35;
      ctx.drawImage(logo, Math.round((CARD_W - logoW) / 2), CARD_H - logoH - 50, logoW, logoH);
      ctx.globalAlpha = 1.0;
    } else {
      ctx.fillStyle = "rgba(255,255,255,0.25)"; ctx.font = "400 12px 'Assistant', sans-serif";
      ctx.letterSpacing = "3px"; ctx.fillText("SWELLSCENES.COM", CARD_W / 2, CARD_H - 40); ctx.letterSpacing = "0px";
    }
    ctx.fillStyle = "rgba(255,255,255,0.18)"; ctx.font = "300 10px 'Assistant', sans-serif";
    ctx.fillText("Image: NASA / ESA / STScI" + (img.year ? " \u00B7 " + img.year : ""), CARD_W / 2, CARD_H - 18);

    return card;
  };

  BirthdayEditor.prototype.loadShareLogo = function () {
    if (this._shareLogo) return Promise.resolve(this._shareLogo);
    var self = this;
    return new Promise(function (resolve) {
      var logo = new Image();
      logo.crossOrigin = "anonymous";
      logo.onload = function () { self._shareLogo = logo; resolve(logo); };
      logo.onerror = function () { resolve(null); };
      var scripts = document.querySelectorAll("script[src*='birthday-editor']");
      var scriptSrc = scripts.length ? scripts[0].src : "";
      var base = scriptSrc ? scriptSrc.replace(/[^/]*$/, "") : "extension/assets/";
      logo.src = base + "swell-logo-white.svg";
    });
  };

  BirthdayEditor.prototype.shareCard = function () {
    if (!this.nasaImage || !this.selectedDate) return;
    var self = this;
    var entry = this.catalog[this.selectedDate.key];
    var img = entry.images[this.selectedImageIndex];
    var monthLabel = MONTHS[this.selectedDate.month - 1];
    var dateStr = monthLabel + " " + this.selectedDate.day;
    var shareUrl = "https://swellscenes.com/pages/hubble-birthday";
    var shareText = "Hubble saw " + img.title + " on " + dateStr + "! See what NASA captured on your birthday \u2192";

    this.loadShareLogo().then(function (logo) {
      self._doShare(logo, img, monthLabel, dateStr, shareUrl, shareText);
    });
  };

  BirthdayEditor.prototype._doShare = function (logo, img, monthLabel, dateStr, fallbackUrl, shareText) {
    var self = this;
    var card = this.generateShareCanvas(logo);
    var dataUrl;
    // JPEG, not PNG: photo-heavy cards exported as PNG are ~2.5-3MB and blow
    // past the server's upload cap; JPEG q0.9 is a few hundred KB.
    try { dataUrl = card.toDataURL("image/jpeg", 0.9); } catch (e) { dataUrl = null; }

    // Upload the share card to the server so social platforms can crawl the og:image
    var hostedShareUrl = null;
    var hostedImageUrl = null;
    var uploadDone = false;

    function buildModal() {
      var shareUrl = hostedShareUrl || fallbackUrl;
      var ogImageUrl = hostedImageUrl;

      var overlay = el("div", "bday-share-overlay");
      var modal = el("div", "bday-share-modal");

      var closeBtn = el("button", "bday-share-modal__close");
      closeBtn.innerHTML = "&times;";
      closeBtn.addEventListener("click", function () { overlay.remove(); });
      overlay.addEventListener("click", function (e) { if (e.target === overlay) overlay.remove(); });
      modal.appendChild(closeBtn);

      var title = el("h2", "bday-share-modal__title");
      title.textContent = "Share Your Space Birthday";
      modal.appendChild(title);

      if (dataUrl) {
        var preview = el("img", "bday-share-modal__preview");
        preview.src = dataUrl;
        preview.alt = "Share card preview";
        modal.appendChild(preview);
      }

      var actions = el("div", "bday-share-modal__actions");

      // Download
      var dlBtn = el("button", "bday-share-action");
      dlBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Download';
      dlBtn.addEventListener("click", function () {
        if (!dataUrl) { showToast("Could not generate image"); return; }
        var a = document.createElement("a"); a.href = dataUrl;
        a.download = "hubble-birthday-" + self.selectedDate.key + ".jpg"; a.click();
        showToast("Image downloaded!");
      });
      actions.appendChild(dlBtn);

      // Facebook
      var fbBtn = el("button", "bday-share-action bday-share-action--fb");
      fbBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> Facebook';
      fbBtn.addEventListener("click", function () {
        window.open("https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(shareUrl) + "&quote=" + encodeURIComponent(shareText), "_blank", "width=600,height=400");
      });
      actions.appendChild(fbBtn);

      // X / Twitter
      var xBtn = el("button", "bday-share-action bday-share-action--x");
      xBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> X';
      xBtn.addEventListener("click", function () {
        window.open("https://twitter.com/intent/tweet?text=" + encodeURIComponent(shareText) + "&url=" + encodeURIComponent(shareUrl), "_blank", "width=600,height=400");
      });
      actions.appendChild(xBtn);

      // Pinterest (uses the hosted image URL directly)
      var pinBtn = el("button", "bday-share-action bday-share-action--pin");
      pinBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/></svg> Pinterest';
      pinBtn.addEventListener("click", function () {
        var mediaUrl = ogImageUrl || shareUrl;
        window.open("https://pinterest.com/pin/create/button/?url=" + encodeURIComponent(shareUrl) + "&description=" + encodeURIComponent(shareText) + "&media=" + encodeURIComponent(mediaUrl), "_blank", "width=600,height=600");
      });
      actions.appendChild(pinBtn);

      // Copy link
      var copyBtn = el("button", "bday-share-action");
      copyBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg> Copy Link';
      copyBtn.addEventListener("click", function () {
        var copyText = shareText + " " + shareUrl;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(copyText).then(function () { showToast("Copied to clipboard!"); });
        } else {
          var ta = document.createElement("textarea"); ta.value = copyText;
          document.body.appendChild(ta); ta.select(); document.execCommand("copy");
          document.body.removeChild(ta); showToast("Copied to clipboard!");
        }
      });
      actions.appendChild(copyBtn);

      // Native share (mobile)
      if (navigator.share) {
        var nativeBtn = el("button", "bday-share-action bday-share-action--native");
        nativeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg> More\u2026';
        nativeBtn.addEventListener("click", function () {
          var shareData = { title: "What did Hubble see on " + dateStr + "?", text: shareText, url: shareUrl };
          if (dataUrl) {
            card.toBlob(function (blob) {
              if (blob) {
                var file = new File([blob], "hubble-birthday.png", { type: "image/png" });
                if (navigator.canShare && navigator.canShare({ files: [file] })) shareData.files = [file];
              }
              navigator.share(shareData).catch(function () {});
            }, "image/png");
          } else {
            navigator.share(shareData).catch(function () {});
          }
        });
        actions.appendChild(nativeBtn);
      }

      modal.appendChild(actions);

      if (!hostedShareUrl) {
        var note = el("p", "bday-share-modal__note");
        note.textContent = "Tip: Download the image and upload it directly for the best result on social media.";
        modal.appendChild(note);
      }

      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    }

    // Upload card to server for og:image hosting. share_base must be an
    // absolute URL: on the storefront proxyBase is the relative App Proxy
    // path (/apps/personalizer), and a relative share link breaks the
    // Facebook/X/Pinterest share intents.
    if (dataUrl) {
      var ogTitle = "Hubble saw " + img.title + " on " + monthLabel + " " + self.selectedDate.day;
      var absShareBase = /^https?:/i.test(self.proxyBase)
        ? self.proxyBase
        : window.location.origin + self.proxyBase;
      fetch(self.proxyBase + "/share/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image: dataUrl,
          title: ogTitle,
          description: img.caption || "",
          date: self.selectedDate.key,
          image_title: img.title,
          share_base: absShareBase
        })
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.share_url) {
          hostedShareUrl = data.share_url;
          hostedImageUrl = data.share_url + "/image.png";
        }
      })
      .catch(function () {})
      .finally(function () { buildModal(); });
    } else {
      buildModal();
    }
  };

  /* ── Add to Cart ───────────────────────────────────────────────────────── */
  BirthdayEditor.prototype.addToCart = function () {
    var self = this;
    this.errorEl.textContent = "";
    this.addBtn.disabled = true;
    this.addBtn.textContent = "Adding\u2026";

    var selectedImg = this.catalog[this.selectedDate.key].images[this.selectedImageIndex];
    var configPayload = Object.assign({}, this.state, {
      template: "nasa-birthday",
      birthday_date: this.selectedDate.key,
      birthday_title: selectedImg.title,
      birthday_image: selectedImg.image
    });

    fetch(this.proxyBase + "/validate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ handle: this.handle, config: configPayload })
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (!res.ok || !res.j.ok) throw new Error(res.j.error || "Invalid configuration");
        var clean = res.j.config;
        clean.handle = self.handle;
        clean.product_key = self.spec.product_key;
        var selImg = self.catalog[self.selectedDate.key].images[self.selectedImageIndex];
        clean.birthday_date = self.selectedDate.key;
        clean.birthday_title = selImg.title;
        clean.birthday_image = selImg.image;
        return self.uploadPreview().then(function (previewUrl) {
          return { clean: clean, previewUrl: previewUrl };
        });
      })
      .then(function (ctx) {
        var clean = ctx.clean;
        var sizeKey = self.state.size || self.state.ratio;
        var styleKey = self.state.style || "Print Only";
        var variantId = self.currentVariantId();
        if (!variantId) throw new Error("No variant configured for " + sizeKey + " / " + styleKey);

        var props = {};
        props["_pz_template"] = "nasa-birthday";
        props["_pz_config"] = JSON.stringify(clean);
        props["Preview"] = ctx.previewUrl;
        props["Birthday"] = MONTHS[self.selectedDate.month - 1] + " " + self.selectedDate.day;
        props["Image"] = clean.birthday_title;
        if (styleKey !== "Print Only") props["Style"] = styleKey;

        var cartUi = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        var payload = { items: [{ id: Number(variantId), quantity: 1, properties: props }] };
        if (cartUi && typeof cartUi.getSectionsToRender === "function") {
          payload.sections = cartUi.getSectionsToRender().map(function (s) { return s.id; }).join(",");
          payload.sections_url = window.location.pathname;
        }
        return fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      })
      .then(function (r) {
        if (!r || !r.ok) return r.json().then(function (j) { throw new Error(j.description || "Add to cart failed"); });
        return r.json();
      })
      .then(function (response) {
        if (response.status) throw new Error(response.description || response.message || "Add to cart failed");
        var cartUi = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        if (cartUi && typeof cartUi.renderContents === "function") {
          if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "birthday-editor", cartData: response });
          }
          cartUi.renderContents(response);
          if (cartUi.classList.contains("is-empty")) cartUi.classList.remove("is-empty");
        }
        self.showCartConfirm();
      })
      .catch(function (e) {
        console.error("[birthday-editor]", e);
        self.errorEl.textContent = e.message;
      })
      .finally(function () {
        self.addBtn.disabled = false;
        self.addBtn.textContent = "Add to cart";
      });
  };

  /* ── Added-to-cart confirmation ─────────────────────────────────────────── */
  BirthdayEditor.prototype.showCartConfirm = function () {
    var self = this;
    var overlay = el("div", "bday-cart-confirm");
    var box = el("div", "bday-cart-confirm__box");

    var check = el("div", "bday-cart-confirm__check");
    check.innerHTML = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    box.appendChild(check);

    // Small snapshot of the customized poster so the confirmation feels personal
    try {
      var thumb = el("img", "bday-cart-confirm__thumb");
      thumb.alt = "Your custom print";
      thumb.src = this.canvasEl.toDataURL("image/jpeg", 0.7);
      box.appendChild(thumb);
    } catch (e) { /* tainted canvas or similar — skip the thumbnail */ }

    var title = el("h3", "bday-cart-confirm__title");
    title.textContent = "Added to your cart!";
    box.appendChild(title);

    var sub = el("p", "bday-cart-confirm__sub");
    var sizeInfo = this.spec.sizes[this.state.size];
    sub.textContent = (this.catalog[this.selectedDate.key].images[this.selectedImageIndex].title || "Your print") +
      " \u00B7 " + (sizeInfo ? sizeInfo.label : this.state.size) +
      (this.state.style && this.state.style !== "Print Only" ? " \u00B7 " + this.state.style : "");
    box.appendChild(sub);

    var actions = el("div", "bday-cart-confirm__actions");

    var checkoutBtn = el("a", "bday-cart-confirm__checkout");
    checkoutBtn.href = "/checkout";
    checkoutBtn.textContent = "Checkout";
    actions.appendChild(checkoutBtn);

    var cartBtn = el("a", "bday-cart-confirm__viewcart");
    cartBtn.href = "/cart";
    cartBtn.textContent = "View Cart";
    actions.appendChild(cartBtn);

    var contBtn = el("button", "bday-cart-confirm__continue");
    contBtn.type = "button";
    contBtn.textContent = "Keep customizing";
    contBtn.addEventListener("click", function () { overlay.remove(); });
    actions.appendChild(contBtn);

    box.appendChild(actions);
    overlay.appendChild(box);
    overlay.addEventListener("click", function (e) { if (e.target === overlay) overlay.remove(); });
    document.body.appendChild(overlay);
  };

  /* ── Boot ───────────────────────────────────────────────────────────────── */
  function boot() {
    document.querySelectorAll("[data-bday-editor]").forEach(function (root) {
      if (root.__bdayBooted) return;
      root.__bdayBooted = true;
      var editor = new BirthdayEditor(root);
      editor.init();
      root.__bdayInstance = editor;
      window.__bdayEditorInstance = editor;

      var autoM = root.getAttribute("data-auto-month");
      var autoD = root.getAttribute("data-auto-day");
      if (autoM && autoD) {
        editor.onDateSelected(parseInt(autoM, 10), parseInt(autoD, 10));
      }
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
